import React, { useEffect, useState } from 'react';
import { Routes, Route, Link, Navigate, useLocation } from 'react-router-dom';
import GlobalNetworkScore from './Dashboard/GlobalNetworkScore';
import AlertsUpdates from './Dashboard/AlertsUpdates';
import MyDependencies from './Dashboard/MyDependencies';
import SecurityInsights from './Dashboard/SecurityInsights';
import PolicyMaturity from './Dashboard/PolicyMaturity';
import CompanySettings from './Dashboard/CompanySettings';
import DashboardTour from './Dashboard/DashboardTour';
import IntelligenceGate from './Dashboard/IntelligenceGate';
import Header from '../components/Header';
import { getUnreadNotificationCount, getUserProfile } from '../services/api';
import '../styles/dashboard.css';

function DashboardLayout() {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [tourActive, setTourActive] = useState(false);
  const [tourStorageKey, setTourStorageKey] = useState(null);
  const [unreadNotifications, setUnreadNotifications] = useState(0);

  useEffect(() => {
    const mainContent = document.querySelector('.dashboard-main');
    if (mainContent) {
      mainContent.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, [location.pathname]);

  useEffect(() => {
    let cancelled = false;

    const prepareTour = async () => {
      try {
        const profile = await getUserProfile();
        const companyId = profile?.company?.id;
        if (!companyId || cancelled) return;

        const storageKey = `risqly-dashboard-tour-v2:${companyId}`;
        setTourStorageKey(storageKey);
        if (!window.localStorage.getItem(storageKey)) {
          setTourActive(true);
          if (window.innerWidth <= 768) setMobileMenuOpen(true);
        }
      } catch (err) {
        console.error('Failed to prepare dashboard tour:', err);
      }
    };

    prepareTour();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const refreshUnreadCount = async () => {
      try {
        const count = await getUnreadNotificationCount();
        if (!cancelled) setUnreadNotifications(count);
      } catch (err) {
        console.error('Failed to load notification count:', err);
      }
    };

    refreshUnreadCount();
    const interval = window.setInterval(refreshUnreadCount, 30000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [location.pathname]);

  // Determine active tab based on current location
  const getActiveTab = () => {
    if (location.pathname.includes('/alerts')) return 'alerts';
    if (location.pathname.includes('/dependencies')) return 'dependencies';
    if (location.pathname.includes('/security-insights')) return 'security-insights';
    if (location.pathname.includes('/policy-maturity') || location.pathname.includes('/policy-scoring-coming-soon')) return 'policy-maturity';
    if (location.pathname.includes('/company-settings')) return 'company-settings';
    return 'global-network';
  };

  const closeMobileMenu = () => setMobileMenuOpen(false);

  return (
    <div className="dashboard-container">
      <Header />
      
      {/* Mobile Menu Button */}
      <button 
        className="mobile-menu-toggle"
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        aria-label="Toggle menu"
      >
        <span className="material-symbols-outlined">menu</span>
      </button>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="mobile-menu-overlay"
          onClick={closeMobileMenu}
        />
      )}
      
      {/* Sidebar */}
      <aside className={`dashboard-sidebar ${mobileMenuOpen ? 'mobile-open' : ''}`}>
        <nav className="sidebar-nav">
          <div className="nav-section">
            <p className="nav-label">Priority</p>
            <Link 
              to="/dashboard/security-insights"
              data-tour="security-insights"
              className={`nav-item nav-item-priority ${getActiveTab() === 'security-insights' ? 'active' : ''}`}
              onClick={closeMobileMenu}
            >
              <span className="material-symbols-outlined">security</span>
              <span>Security Insights</span>
            </Link>
            <Link 
              to="/dashboard"
              data-tour="global-network"
              className={`nav-item ${getActiveTab() === 'global-network' ? 'active' : ''}`}
              onClick={closeMobileMenu}
            >
              <span className="material-symbols-outlined">hub</span>
              <span>Global Network Score</span>
            </Link>
          </div>

          <div className="nav-section">
            <p className="nav-label">Operations</p>
            <Link 
              to="/dashboard/policy-maturity"
              data-tour="policy-maturity"
              className={`nav-item ${getActiveTab() === 'policy-maturity' ? 'active' : ''}`}
              onClick={closeMobileMenu}
            >
              <span className="material-symbols-outlined">verified_user</span>
              <span>Policy Maturity</span>
            </Link>
            <Link 
              to="/dashboard/alerts"
              data-tour="alerts"
              className={`nav-item ${getActiveTab() === 'alerts' ? 'active' : ''}`}
              onClick={closeMobileMenu}
            >
              <span className="material-symbols-outlined">list_alt</span>
              <span>Notifications</span>
              {unreadNotifications > 0 && (
                <span className="alert-badge">{Math.min(unreadNotifications, 99)}</span>
              )}
            </Link>
            <Link 
              to="/dashboard/dependencies"
              data-tour="dependencies"
              className={`nav-item ${getActiveTab() === 'dependencies' ? 'active' : ''}`}
              onClick={closeMobileMenu}
            >
              <span className="material-symbols-outlined">account_tree</span>
              <span>My Dependencies</span>
            </Link>
          </div>

          <div className="nav-section">
            <p className="nav-label">Account</p>
            <Link
              to="/dashboard/company-settings"
              data-tour="company-settings"
              className={`nav-item ${getActiveTab() === 'company-settings' ? 'active' : ''}`}
              onClick={closeMobileMenu}
            >
              <span className="material-symbols-outlined">settings</span>
              <span>Company Settings</span>
            </Link>
          </div>
        </nav>
        <div className="dashboard-tour-replay-wrap">
          <button
            type="button"
            className="dashboard-tour-replay"
            onClick={() => {
              setTourActive(true);
              if (window.innerWidth <= 768) setMobileMenuOpen(true);
            }}
          >
            <span className="material-symbols-outlined">help</span>
            <span>Replay dashboard tour</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="dashboard-main">
        <section className="dashboard-content">
          <Routes>
            <Route path="/" element={<GlobalNetworkScore />} />
            <Route path="/alerts" element={<AlertsUpdates />} />
            <Route path="/dependencies" element={<MyDependencies />} />
            <Route
              path="/security-insights"
              element={<IntelligenceGate><SecurityInsights /></IntelligenceGate>}
            />
            <Route path="/policy-maturity" element={<PolicyMaturity />} />
            <Route path="/policy-scoring-coming-soon" element={<Navigate to="/dashboard/policy-maturity" replace />} />
            <Route path="/company-settings" element={<CompanySettings />} />
          </Routes>
        </section>
      </main>

      <DashboardTour
        active={tourActive}
        onClose={() => {
          if (tourStorageKey) window.localStorage.setItem(tourStorageKey, 'completed');
          setTourActive(false);
          setMobileMenuOpen(false);
        }}
      />
    </div>
  );
}

function Dashboard() {
  return <DashboardLayout />;
}

export default Dashboard;
